import { CENTIMETERS_TO_INCHES, INCHES_TO_FEET, METERS_TO_INCHES, UD_DESCRIPTIONS, UD_TYPES } from './constants';
import { compose, getUdDescriptionInGrammarTypeByLang, isCatalogUSA, throwError, toFraction } from './utils';

const _metersToInches = (m: number) => +(m / METERS_TO_INCHES);
const _centimetersToInches = (cm: number) => +(cm / CENTIMETERS_TO_INCHES);

const _toFeetAndInches = (totalInches: number) => {
  const feet = Math.floor(totalInches / INCHES_TO_FEET);
  const inches = +(totalInches - INCHES_TO_FEET * feet);

  return { feet, inches };
};

const _toInchesAndFraction = (totalInches: number) => ({ inches: totalInches, fraction: toFraction(totalInches) });

const UD_TYPES_TO_INCHES = {
  [UD_TYPES.CENTIMETERS]: {
    toInches: _centimetersToInches,
    toFeetAndInches: compose(_toFeetAndInches, _centimetersToInches),
    toInchesFraction: compose(toFraction, _centimetersToInches),
    toInchesAndFraction: compose(_toInchesAndFraction, _centimetersToInches),
  },
  [UD_TYPES.METERS]: {
    toInches: _metersToInches,
    toFeetAndInches: compose(_toFeetAndInches, _metersToInches),
    toInchesFraction: compose(toFraction, _metersToInches),
    toInchesAndFraction: compose(_toInchesAndFraction, _metersToInches),
  },
};

type TUdType = UD_TYPES.CENTIMETERS | UD_TYPES.METERS;

export const toInches = (value: number, udType: TUdType) => {
  if (!UD_TYPES_TO_INCHES[udType]) {
    throwError();
  }

  return UD_TYPES_TO_INCHES[udType].toInches(value);
};

export const toFeetAndInches = (value: number, udType: TUdType) => {
  if (!UD_TYPES_TO_INCHES[udType]) {
    throwError();
  }

  return UD_TYPES_TO_INCHES[udType].toFeetAndInches(value);
};

export const toInchesFraction = (value: number, udType: TUdType) => {
  if (!UD_TYPES_TO_INCHES[udType]) {
    throwError();
  }

  return UD_TYPES_TO_INCHES[udType].toInchesFraction(value);
};

export const toInchesAndFraction = (value: number, udType: TUdType) => {
  if (!UD_TYPES_TO_INCHES[udType]) {
    throwError();
  }

  return UD_TYPES_TO_INCHES[udType].toInchesAndFraction(value);
};

export const toObjectLengthByCatalog = (catalogId: number, value: number, udType: TUdType) => {
  if (!UD_TYPES_TO_INCHES[udType]) {
    throwError();
  }

  const inchesAndFraction = isCatalogUSA(catalogId) ? toInchesAndFraction(value, udType) : undefined;

  const enDescription = inchesAndFraction
    ? `${inchesAndFraction.fraction} ${getUdDescriptionInGrammarTypeByLang(
        inchesAndFraction.inches,
        UD_DESCRIPTIONS[UD_TYPES.INCHES].en
      )}`
    : `${value} ${getUdDescriptionInGrammarTypeByLang(value, UD_DESCRIPTIONS[udType].en)}`;

  const esDescription = inchesAndFraction
    ? `${inchesAndFraction.fraction} ${getUdDescriptionInGrammarTypeByLang(
        inchesAndFraction.inches,
        UD_DESCRIPTIONS[UD_TYPES.INCHES].es
      )}`
    : `${value} ${getUdDescriptionInGrammarTypeByLang(value, UD_DESCRIPTIONS[udType].es)}`;

  const shortDescription = inchesAndFraction
    ? `${inchesAndFraction.fraction}${UD_DESCRIPTIONS[UD_TYPES.INCHES].short}`
    : `${value} ${UD_DESCRIPTIONS[udType].short}`;

  return {
    value,
    ud: udType,
    description: {
      en: enDescription,
      es: esDescription,
      de: enDescription,
      fr: enDescription,
      it: enDescription,
      us: enDescription,
    },
    shortDescription: {
      en: shortDescription,
      es: shortDescription,
      de: shortDescription,
      fr: shortDescription,
      it: shortDescription,
      us: shortDescription,
    },
  };
};
